package com.evoapps.evoshift.domain.usecase.availability

import com.evoapps.evoshift.domain.capacity.monthRange
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.schedule.DayAnswerSummary
import com.evoapps.evoshift.domain.schedule.DayAnswers
import kotlinx.datetime.LocalDate

/** SH-172: what a month's answers are read from: the team, their forms, approved leave, the slots. */
data class MonthAnswers(
    val workplaceId: String,
    val team: List<User>,
    /** Each person's form for the month (missing: none). */
    val availability: Map<String, Availability>,
    val absences: List<Absence>,
    val slots: List<AvailabilitySlot>,
    /** The month was asked for (its roster links a request). */
    val requested: Boolean
) {
    /** Anything to show: the month was asked for, or someone already answered. */
    val shows: Boolean get() = requested || availability.values.any { it.isFilled || it.days.isNotEmpty() }

    /** [date]'s answers against the day's [dayShifts] (see [DayAnswers.of]). */
    fun day(date: LocalDate, dayShifts: List<Shift>): DayAnswerSummary =
        DayAnswers.of(date, team, workplaceId, availability, slots, absences, dayShifts)
}

/**
 * SH-172: the team's availability for [Roster]'s month, as the skeleton editor shows it while the
 * demand is built: the forms linked to the roster and, for anyone without one, their form for the
 * month at the workplace (filled before the roster existed), as the capacity reads them (SH-48).
 * Managers may read all of it (firestore.rules: availability, absences, users).
 */
class GetMonthAnswersUseCase(
    private val userRepository: UserRepository,
    private val availabilityRepository: AvailabilityRepository,
    private val absenceRepository: AbsenceRepository,
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(roster: Roster): Result<MonthAnswers> = runCatching {
        val workplace = workplaceRepository.getWorkplace(roster.workplaceId).getOrThrow()
        val team = userRepository.getAssignableEmployees(roster.workplaceId).getOrThrow()
            .filter { it.engagements[roster.workplaceId]?.isActive == true }
        val linked = availabilityRepository.getAvailabilityForRoster(roster.id).getOrDefault(emptyList())
            .filter { it.workplaceId == null || it.workplaceId == roster.workplaceId }
            .associateBy { it.userId }
        val month = roster.getMonthYear()
        val availability = team.mapNotNull { user ->
            (linked[user.uid] ?: availabilityRepository.findAvailability(user.uid, roster.workplaceId, month).getOrNull())
                ?.let { user.uid to it }
        }.toMap()
        val range = monthRange(roster.year, roster.month)
        val absences = absenceRepository.getAbsencesForRoster(roster.workplaceId, range.start.toString(), range.endInclusive.toString())
            .getOrDefault(emptyList())
        MonthAnswers(
            workplaceId = roster.workplaceId,
            team = team,
            availability = availability,
            absences = absences,
            slots = workplace.settings.definedAvailabilitySlots,
            requested = roster.availabilityRequestId != null
        )
    }
}
